import api from '../../utils/api'
import { useState, useEffect, useRef } from "react"
import { FiInbox, FiLayers, FiSliders, FiPackage, FiCheckCircle } from 'react-icons/fi'
import { FaCheck, FaQrcode } from 'react-icons/fa'
import { MdQrCodeScanner } from 'react-icons/md'
import { QRCodeSVG } from 'qrcode.react'

/* form */
import Input from "../../shared/components/form/input"
import Radio from "../../shared/components/form/Radio"
import Button from "../../shared/components/Button/Button"
import Modal from '../../shared/modal/Modal'

/* Hooks */
import useMyPermission from '../../hooks/useMyPermission'
import useFlashMessage from '../../hooks/useFlashMessage'

import NewStockLot from './NewStockLot'

import './Stock.css'

function Stock() {

    const [token] = useState(localStorage.getItem('token') || '')
    const { setFlashMessage } = useFlashMessage()
    const [saldo, setSaldo] = useState([])
    const [lots, setLots] = useState([])
    const [pendingPOs, setPendingPOs] = useState([])
    const [pendingModal, setPendingModal] = useState(false)
    const [load, setLoad] = useState(false)

    const [modalNewLot, setModalNewLot] = useState(false)
    const [prefillLot, setPrefillLot] = useState(null)
    const [lotsGroup, setLotsGroup] = useState(null)
    const [movementLotId, setMovementLotId] = useState(null)
    const [movementForm, setMovementForm] = useState({ quantity: '', reason: '' })

    const [adjustGroup, setAdjustGroup] = useState(null)
    const [countedQuantity, setCountedQuantity] = useState('')
    const [shortageMode, setShortageMode] = useState('fifo')
    const [selectedLotId, setSelectedLotId] = useState('')
    const [surplusMode, setSurplusMode] = useState('unknown')

    const [qrLot, setQrLot] = useState(null)
    const [scanModal, setScanModal] = useState(false)
    const [scanValue, setScanValue] = useState('')
    const scanInputRef = useRef(null)

    /* ---------------------- Permission ------------------------- */
    const [myPermission, setMyPermission] = useState({ group: "", })
    const { checkPermission } = useMyPermission()
    const myPermissionView = checkPermission(myPermission, "stock_view")
    const myPermissionCreate = checkPermission(myPermission, "stock_create")
    const myPermissionEdit = checkPermission(myPermission, "stock_edit")

    const getAll = () => {
        Promise.all([
            api.get('/stock/saldo', { headers: { Authorization: `Bearer ${JSON.parse(token)}` } }),
            api.get('/stock/lot/getall', { headers: { Authorization: `Bearer ${JSON.parse(token)}` } }),
            api.get('/purchase/order/getPending', { headers: { Authorization: `Bearer ${JSON.parse(token)}` } }),
        ]).then(([saldoRes, lotsRes, pendingRes]) => {
            setSaldo(saldoRes.data.saldo)
            setMyPermission(saldoRes.data.permission)
            setLots(lotsRes.data.lots)
            setPendingPOs(pendingRes.data.purchaseOrders || [])
            setLoad(true)
        })
    }

    // Conferir o que chegou fisicamente e dar entrada no Estoque é uma ação de quem recebe a
    // mercadoria, não de quem fez a compra — por isso mora aqui, e não em Compras.
    async function confirmReceipt(id) {
        let msgType = 'success'
        const data = await api.patch(`/purchase/order/receive/${id}`, {}, {
            headers: { Authorization: `Bearer ${JSON.parse(token)}` },
        }).then((response) => response.data).catch((err) => { msgType = 'error'; return err.response.data })

        setFlashMessage(data.message, msgType)
        if (msgType !== 'error') {
            getAll()
        }
    }

    useEffect(() => {
        getAll()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token])

    function materialOf(entity) {
        return entity.materialType === 'aviamento' ? entity.aviamento : entity.cloth
    }

    function groupKey(entity) {
        const material = materialOf(entity)
        return `${entity.materialType}_${material?._id}_${entity.provider?._id || '-'}_${entity.unit.value}`
    }

    function openLotsGroup(group) {
        setLotsGroup(group)
        setMovementLotId(null)
    }

    useEffect(() => {
        if (scanModal) {
            scanInputRef.current?.focus()
        }
    }, [scanModal])

    function handleScanSubmit(e) {
        e.preventDefault()
        const lot = lots.find((l) => l._id === scanValue.trim())
        if (!lot) {
            setFlashMessage('Código não corresponde a nenhum lote em estoque.', 'error')
            setScanValue('')
            return
        }
        setScanModal(false)
        setScanValue('')
        setLotsGroup({ materialType: lot.materialType, cloth: lot.cloth, aviamento: lot.aviamento, provider: lot.provider, unit: lot.unit })
        setMovementLotId(myPermissionEdit && lot.remainingQuantity > 0 ? lot._id : null)
    }

    const groupLots = lotsGroup
        ? lots.filter((l) => groupKey(l) === groupKey(lotsGroup))
        : []

    function openAdjust(group) {
        setAdjustGroup(group)
        setCountedQuantity('')
        setShortageMode('fifo')
        setSelectedLotId('')
        setSurplusMode('unknown')
    }

    const adjustGroupLots = adjustGroup
        ? lots.filter((l) => groupKey(l) === groupKey(adjustGroup) && l.remainingQuantity > 0)
        : []

    const adjustDelta = adjustGroup && countedQuantity !== '' && !isNaN(parseFloat(countedQuantity))
        ? parseFloat(countedQuantity) - adjustGroup.totalQuantity
        : null

    async function submitAdjust() {
        if (adjustDelta === null) return

        if (adjustDelta > 0 && surplusMode === 'real') {
            // Sobra com origem conhecida não usa o endpoint de ajuste — abre o fluxo normal de
            // "Registrar Lote" já pré-preenchido, pra pessoa completar fornecedor/custo reais.
            setPrefillLot({
                materialType: adjustGroup.materialType,
                cloth: adjustGroup.cloth,
                aviamento: adjustGroup.aviamento,
                provider: adjustGroup.provider,
                unit: adjustGroup.unit,
                originalQuantity: adjustDelta,
                origin: 'comprado',
                entryDate: new Date().toISOString().slice(0, 10),
            })
            setAdjustGroup(null)
            setModalNewLot(true)
            return
        }

        let msgType = 'success'
        const body = {
            materialType: adjustGroup.materialType,
            cloth: adjustGroup.cloth,
            aviamento: adjustGroup.aviamento,
            provider: adjustGroup.provider,
            unit: adjustGroup.unit,
            countedQuantity: parseFloat(countedQuantity),
            lotId: adjustDelta < 0 && shortageMode === 'manual' ? selectedLotId : undefined,
        }

        const data = await api.patch('/stock/saldo/adjust', body, {
            headers: { Authorization: `Bearer ${JSON.parse(token)}` },
        }).then((response) => response.data).catch((err) => { msgType = 'error'; return err.response.data })

        setFlashMessage(data.message, msgType)
        if (msgType !== 'error') {
            setAdjustGroup(null)
            getAll()
        }
    }

    async function registerMovement(lotId) {
        let msgType = 'success'
        const data = await api.patch(`/stock/lot/movement/${lotId}`, { type: 'saida', quantity: parseFloat(movementForm.quantity), reason: movementForm.reason }, {
            headers: { Authorization: `Bearer ${JSON.parse(token)}` },
        }).then((response) => {
            return response.data
        }).catch((err) => {
            msgType = 'error'
            return err.response.data
        })

        setFlashMessage(data.message, msgType)
        if (msgType !== 'error') {
            setMovementLotId(null)
            setMovementForm({ quantity: '', reason: '' })
            getAll()
            setLotsGroup(null)
        }
    }

    if (!load) {
        return <div className="pg-loading">Carregando estoque...</div>
    }

    return myPermissionView ? (
        <section className="pg-page">

            <div className="pg-sticky-top">
                <div className="pg-header">
                    <div className="pg-header-title">
                        <p className="pg-title">Estoque de Matéria-Prima</p>
                        <span className="pg-title-accent" />
                    </div>
                    <div className="pg-header-row">
                        <div className="pg-header-actions">
                            {myPermissionCreate && (
                                <Button variant="new" onClick={() => setModalNewLot(true)}>Registrar Lote</Button>
                            )}
                            <Button variant="select" icon={MdQrCodeScanner} onClick={() => setScanModal(true)}>Ler Código</Button>
                            <Button variant="finish" icon={FiPackage} onClick={() => setPendingModal(true)}>
                                Recebimentos Pendentes{pendingPOs.length > 0 ? ` (${pendingPOs.length})` : ''}
                            </Button>
                        </div>
                        <div className="pg-header-right">
                            <span className="pg-count">
                                {saldo.length} {saldo.length === 1 ? 'material' : 'materiais'}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="pg-list">
                {saldo.length > 0 ? (
                    saldo.map((g) => (
                        <div key={groupKey(g)} className="pg-item">
                            <div className="pg-item-main">
                                <div className="stock-row-text">
                                    <div className="stock-row-header">
                                        <span className="stock-row-name">{materialOf(g).name}</span>
                                        {g.materialType === 'aviamento' && <span className="stock-row-material-tag">Aviamento</span>}
                                    </div>
                                    <div className="stock-row-links">
                                        {g.provider && <span className="stock-row-link-item">{g.provider.name}</span>}
                                        <span className="stock-row-link-item">Custo médio: R$ {g.avgCost.toFixed(2)}/{g.unit.value}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="stock-row-saldo-col">
                                <span className="stock-row-saldo-caption">Saldo</span>
                                <span className="stock-row-saldo-value">{g.totalQuantity.toFixed(2)} {g.unit.label || g.unit.value}</span>
                            </div>

                            <div className="shd-row-box-item-action">
                                <FiLayers className="stock-icon-row-lots" title="Ver Lotes" onClick={() => openLotsGroup(g)} />
                                {myPermissionEdit && (
                                    <FiSliders className="stock-icon-row-adjust" title="Ajustar Inventário" onClick={() => openAdjust(g)} />
                                )}
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="pg-empty">
                        <FiInbox size={32} className="pg-empty-icon" />
                        <p className="pg-empty-title">Nenhum material em estoque</p>
                        <p className="pg-empty-subtitle">Registre o primeiro lote para começar.</p>
                    </div>
                )}
            </div>

            <Modal
                modalStatus={modalNewLot}
                changeStatus={(status) => { setModalNewLot(status); if (!status) setPrefillLot(null) }}
                w={80}
                content={
                    <NewStockLot
                        btCancelAction={() => { setModalNewLot(false); setPrefillLot(null) }}
                        handleOnSuccess={() => { setModalNewLot(false); setPrefillLot(null); getAll() }}
                        initialLot={prefillLot}
                    />
                }
            />

            <Modal
                modalStatus={!!lotsGroup}
                changeStatus={() => setLotsGroup(null)}
                w={60}
                content={
                    lotsGroup && (
                        <div className="stock-lots">
                            <div className="stock-lots-header">
                                <p className="stock-lots-title">Lotes — {materialOf(lotsGroup).name}{lotsGroup.provider ? ` (${lotsGroup.provider.name})` : ''}</p>
                                <span className="stock-lots-title-accent" />
                            </div>
                            {groupLots.map((lot) => (
                                <div key={lot._id} className="stock-lot-item">
                                    <div className="stock-lot-item-main">
                                        <span className={`stock-lot-origin stock-lot-origin-${lot.origin}`}>
                                            {lot.origin === 'sobra' ? 'Sobra reaproveitada' : lot.origin === 'ajuste' ? 'Ajuste — custo desconhecido' : 'Comprado agora'}
                                        </span>
                                        {lot.originReference && <span className="stock-lot-ref">{lot.originReference}</span>}
                                        <span className="stock-lot-qty">{lot.remainingQuantity.toFixed(2)} / {lot.originalQuantity.toFixed(2)} {lot.unit.value}</span>
                                        <span className="stock-lot-cost">R$ {lot.unitCost.toFixed(2)}/{lot.unit.value}</span>
                                        <FaQrcode className="stock-icon-row-qr" title="Gerar Código" onClick={() => setQrLot(lot)} />
                                        {myPermissionEdit && lot.remainingQuantity > 0 && (
                                            <button type="button" className="stock-lot-baixa-btn" onClick={() => setMovementLotId(movementLotId === lot._id ? null : lot._id)}>
                                                Dar Baixa
                                            </button>
                                        )}
                                    </div>
                                    {movementLotId === lot._id && (
                                        <div className="stock-lot-movement-form">
                                            <Input text="Quantidade" type="number" name="quantity" placeholder="0.00" handleOnChange={(e) => setMovementForm({ ...movementForm, quantity: e.target.value })} value={movementForm.quantity} />
                                            <Input text="Motivo" type="text" name="reason" placeholder="Ex.: consumo, perda, ajuste" handleOnChange={(e) => setMovementForm({ ...movementForm, reason: e.target.value })} value={movementForm.reason} />
                                            <button type="button" className="stock-lot-baixa-confirm" onClick={() => registerMovement(lot._id)}>
                                                <FaCheck size={13} /> Confirmar
                                            </button>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )
                }
            />

            <Modal
                modalStatus={!!adjustGroup}
                changeStatus={() => setAdjustGroup(null)}
                w={50}
                content={
                    adjustGroup && (
                        <div className="stock-adjust">
                            <div className="stock-adjust-header">
                                <p className="stock-adjust-title">Ajustar Inventário — {materialOf(adjustGroup).name}</p>
                                <span className="stock-adjust-title-accent" />
                            </div>

                            <p className="stock-adjust-current">
                                Saldo no sistema: <strong>{adjustGroup.totalQuantity.toFixed(2)} {adjustGroup.unit.label || adjustGroup.unit.value}</strong>
                            </p>

                            <Input
                                text="Quantidade contada fisicamente"
                                type="number"
                                name="countedQuantity"
                                placeholder="0.00"
                                handleOnChange={(e) => setCountedQuantity(e.target.value)}
                                value={countedQuantity}
                            />

                            {adjustDelta !== null && (
                                Math.abs(adjustDelta) < 0.0001 ? (
                                    <p className="stock-adjust-match">Estoque já bate — nenhum ajuste necessário.</p>
                                ) : adjustDelta < 0 ? (
                                    <div className="stock-adjust-choice">
                                        <p className="stock-adjust-choice-label">
                                            Falta {Math.abs(adjustDelta).toFixed(2)} {adjustGroup.unit.value} — de qual lote tirar?
                                        </p>
                                        <Radio
                                            options={[
                                                { value: 'fifo', label: 'Lote mais antigo (automático)' },
                                                { value: 'manual', label: 'Escolher lote manualmente' },
                                            ]}
                                            name="shortageMode"
                                            value={shortageMode}
                                            defaultValue="fifo"
                                            handleOnChange={(name, value) => setShortageMode(value)}
                                        />
                                        {shortageMode === 'manual' && (
                                            <select className="input stock-adjust-lot-select" value={selectedLotId} onChange={(e) => setSelectedLotId(e.target.value)}>
                                                <option value="">Selecione o lote...</option>
                                                {adjustGroupLots.map((lot) => (
                                                    <option key={lot._id} value={lot._id}>
                                                        {lot.origin === 'sobra' ? 'Sobra' : 'Comprado'} — {lot.remainingQuantity.toFixed(2)} {lot.unit.value} — R$ {lot.unitCost.toFixed(2)}/{lot.unit.value}
                                                    </option>
                                                ))}
                                            </select>
                                        )}
                                    </div>
                                ) : (
                                    <div className="stock-adjust-choice">
                                        <p className="stock-adjust-choice-label">
                                            Sobra de {adjustDelta.toFixed(2)} {adjustGroup.unit.value} — a origem é conhecida?
                                        </p>
                                        <Radio
                                            options={[
                                                { value: 'real', label: 'Sim, registrar como lote de compra real' },
                                                { value: 'unknown', label: 'Não, origem desconhecida' },
                                            ]}
                                            name="surplusMode"
                                            value={surplusMode}
                                            defaultValue="unknown"
                                            handleOnChange={(name, value) => setSurplusMode(value)}
                                        />
                                    </div>
                                )
                            )}

                            <div className="stock-adjust-actions">
                                <Button variant="cancel" onClick={() => setAdjustGroup(null)}>Cancelar</Button>
                                <Button
                                    variant="save"
                                    onClick={submitAdjust}
                                    disabled={adjustDelta === null || Math.abs(adjustDelta) < 0.0001 || (adjustDelta < 0 && shortageMode === 'manual' && !selectedLotId)}
                                >
                                    Confirmar
                                </Button>
                            </div>
                        </div>
                    )
                }
            />

            <Modal
                modalStatus={scanModal}
                changeStatus={setScanModal}
                w={36}
                h={30}
                title="Ler Código do Lote"
                content={
                    <form className="stock-scan-box" onSubmit={handleScanSubmit}>
                        <MdQrCodeScanner size={40} className="stock-scan-icon" />
                        <p className="stock-scan-hint">Aponte o leitor para o código do lote</p>
                        <input
                            ref={scanInputRef}
                            className="input"
                            type="text"
                            name="scanValue"
                            placeholder="Aguardando leitura..."
                            onChange={(e) => setScanValue(e.target.value)}
                            value={scanValue}
                        />
                    </form>
                }
            />

            <Modal
                modalStatus={!!qrLot}
                changeStatus={() => setQrLot(null)}
                w={34}
                h={46}
                title="Código do Lote"
                content={
                    qrLot && (
                        <div className="stock-qr-box">
                            <div className="stock-qr-print-area">
                                <QRCodeSVG value={qrLot._id} size={200} />
                                <p className="stock-qr-label">{materialOf(qrLot).name}{qrLot.provider ? ` — ${qrLot.provider.name}` : ''}</p>
                                <p className="stock-qr-label-sub">{qrLot.remainingQuantity.toFixed(2)} {qrLot.unit.value}{qrLot.originReference ? ` · ${qrLot.originReference}` : ''}</p>
                            </div>
                            <Button variant="config" onClick={() => window.print()}>Imprimir</Button>
                        </div>
                    )
                }
            />

            <Modal
                modalStatus={pendingModal}
                changeStatus={() => setPendingModal(false)}
                w={60}
                content={
                    <div className="stock-pending">
                        <div className="stock-pending-header">
                            <p className="stock-pending-title">Recebimentos Pendentes</p>
                            <span className="stock-pending-title-accent" />
                        </div>

                        {pendingPOs.length > 0 ? (
                            pendingPOs.map((po) => (
                                <div key={po._id} className="stock-pending-item">
                                    <div className="stock-pending-item-main">
                                        <span className="stock-pending-order">Pedido #{po.order?.code} — {po.order?.client?.name}</span>
                                        <span className="stock-pending-provider">{po.provider?.name}</span>
                                        <span className="stock-lot-ref">
                                            {po.items.map((it) => it.materialType === 'aviamento' ? it.aviamento?.name : it.cloth?.name).filter(Boolean).join(', ')}
                                        </span>
                                        {po.expectedArrival && (
                                            <span className="stock-lot-ref">Previsão: {new Date(po.expectedArrival).toLocaleDateString('pt-BR')}</span>
                                        )}
                                        <span className="stock-lot-qty">R$ {po.totalValue.toFixed(2)}</span>
                                        {myPermissionEdit && (
                                            <button type="button" className="stock-lot-baixa-confirm" onClick={() => confirmReceipt(po._id)}>
                                                <FiCheckCircle size={13} /> Confirmar Recebimento
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))
                        ) : (
                            <p className="stock-pending-empty">Nenhum recebimento pendente.</p>
                        )}
                    </div>
                }
            />

        </section>
    ) : (
        <div className="pg-empty">
            <p className="pg-empty-title">Sem permissão para visualizar o estoque</p>
        </div>
    )
}

export default Stock
